/**
 * Google My Business Worker API utilities for admin dashboard
 * 
 * This module provides direct access to the GMB Worker microservice
 * which handles all Google Business Profile interactions.
 * 
 * Worker URL: https://fixmybrick-gmb-worker.floral-breeze-0a38.workers.dev
 * 
 * Supported Actions (19 total):
 * - Account Management: oauth_url, oauth_callback, list_accounts, list_locations, discover_all, get_stored_accounts
 * - Reviews: get_reviews, reply_review, delete_reply, batch_get_reviews  
 * - Posts: get_posts, create_post, update_post, delete_post
 * - Analytics: get_insights, get_performance_metrics, get_search_keywords
 * - Q&A: get_questions, answer_question
 * - Media: get_media
 */

// GMB Worker base URL
const GMB_WORKER_URL = 'https://fixmybrick-gmb-worker.floral-breeze-0a38.workers.dev'

// In development, use a test token
const DEV_TEST_TOKEN = 'test-token-for-development'

export type GMBWorkerAction = 
  | 'oauth_url' 
  | 'oauth_callback' 
  | 'list_accounts' 
  | 'list_locations' 
  | 'get_reviews' 
  | 'get_insights' 
  | 'create_post' 
  | 'reply_review' 
  | 'discover_all' 
  | 'get_stored_accounts'
  | 'get_posts'
  | 'update_post'
  | 'delete_post'
  | 'get_performance_metrics'
  | 'get_search_keywords'
  | 'get_media'
  | 'get_questions'
  | 'answer_question'
  | 'delete_reply'
  | 'batch_get_reviews'

export interface GMBWorkerRequest {
  action: GMBWorkerAction
  params?: {
    // Account & Location params
    account_id?: string
    location_id?: string
    location_ids?: string[]
    
    // Review params
    review_id?: string
    reply_comment?: string
    
    // Pagination params
    page_size?: number
    page_token?: string
    
    // Time range params
    days?: number
    start_date?: string
    end_date?: string
    
    // OAuth params
    code?: string
    state?: string
    
    // Post params
    post_id?: string
    post?: {
      languageCode: string
      summary: string
      topicType: 'STANDARD' | 'EVENT' | 'OFFER' | 'PRODUCT'
      callToAction?: {
        actionType: string
        url?: string
      }
      media?: Array<{
        mediaFormat: 'PHOTO' | 'VIDEO'
        sourceUrl: string
      }>
    }
    
    // Q&A params
    question_id?: string
    answer?: string
    
    // Analytics params
    metric_requests?: Array<{
      metric: string
      options?: any
    }>
  }
}

export interface GMBWorkerResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  message?: string
  cached?: boolean
  rateLimit?: {
    remaining: number
    resetTime: number
  }
}

/**
 * Make a request to the GMB Worker microservice
 */
export async function callGMBWorker<T = any>(
  action: GMBWorkerAction, 
  params?: GMBWorkerRequest['params']
): Promise<GMBWorkerResponse<T>> {
  try {
    // Always use test token for now (development mode)
    const token = DEV_TEST_TOKEN
    
    console.log(`GMB Worker [${action}] request:`, { action, params })
    
    const response = await fetch(GMB_WORKER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action,
        params
      }),
    })

    if (!response.ok) {
      console.error(`GMB Worker [${action}] HTTP error:`, response.status, response.statusText)
      const errorText = await response.text()
      console.error(`GMB Worker [${action}] error body:`, errorText)
      return {
        success: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
        message: `Failed to call GMB Worker action: ${action}`
      }
    }

    const result = await response.json()
    
    // Log response for debugging
    console.log(`GMB Worker [${action}] response:`, result)
    
    return result
  } catch (error) {
    console.error(`GMB Worker API error [${action}]:`, error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      message: `Failed to call GMB Worker action: ${action}`
    }
  }
}

// ===== Account Management Actions =====

/**
 * Generate OAuth URL for GMB authorization
 */
export async function generateOAuthUrl() {
  return callGMBWorker<{
    auth_url: string
    redirect_uri: string
    instructions: string
  }>('oauth_url')
}

/**
 * Handle OAuth callback with authorization code
 */
export async function handleOAuthCallback(code: string, state?: string) {
  return callGMBWorker('oauth_callback', { code, state })
}

/**
 * Discover all GMB accounts and locations
 */
export async function discoverAllAccounts() {
  return callGMBWorker<{
    accounts: Array<{
      id: string
      name: string
      type: string
      role: string
      state: string
      location_count: number
      discovered_at: string
    }>
    locations: Array<{
      id: string
      account_id: string
      account_name: string
      name: string
      address: string
      phone?: string
      website?: string
      place_id?: string
      lat_lng?: {
        latitude: number
        longitude: number
      }
    }>
    total_accounts: number
    total_locations: number
    discovered_at: string
  }>('discover_all')
}

/**
 * Get stored accounts and locations from cache
 */
export async function getStoredAccounts() {
  return callGMBWorker('get_stored_accounts')
}

/**
 * List all GMB accounts
 */
export async function listAccounts() {
  return callGMBWorker<{
    accounts: Array<{
      name: string
      accountName: string
      type: string
      role: string
      state: string
      accountNumber?: string
    }>
  }>('list_accounts')
}

/**
 * List locations for a specific account
 */
export async function listLocations(accountId: string) {
  return callGMBWorker<{
    locations: Array<{
      name: string
      locationName: string
      primaryPhone?: string
      websiteUri?: string
      address?: any
      latlng?: {
        latitude: number
        longitude: number
      }
      placeId?: string
    }>
  }>('list_locations', { account_id: accountId })
}

// ===== Reviews Management Actions =====

/**
 * Get reviews for a location
 */
export async function getReviews(
  locationId: string, 
  pageSize: number = 50,
  pageToken?: string
) {
  return callGMBWorker<{
    reviews: Array<{
      name: string
      reviewId: string
      reviewer: {
        profilePhotoUrl?: string
        displayName: string
        isAnonymous?: boolean
      }
      starRating: 'ONE' | 'TWO' | 'THREE' | 'FOUR' | 'FIVE'
      comment?: string
      createTime: string
      updateTime: string
      reviewReply?: {
        comment: string
        updateTime: string
      }
    }>
    nextPageToken?: string
    averageRating?: number
    totalReviewCount?: number
  }>('get_reviews', { 
    location_id: locationId, 
    page_size: pageSize,
    page_token: pageToken 
  })
}

/**
 * Reply to a review
 */
export async function replyToReview(reviewId: string, comment: string) {
  return callGMBWorker('reply_review', {
    review_id: reviewId,
    reply_comment: comment
  })
}

/**
 * Delete a review reply
 */
export async function deleteReviewReply(reviewId: string) {
  return callGMBWorker('delete_reply', {
    review_id: reviewId
  })
}

/**
 * Get reviews from multiple locations at once
 */
export async function batchGetReviews(locationIds: string[], pageSize: number = 5) {
  return callGMBWorker<{
    results: Array<{
      locationId: string
      success: boolean
      reviews?: any[]
      error?: string
    }>
  }>('batch_get_reviews', {
    location_ids: locationIds,
    page_size: pageSize
  })
}

// ===== Posts Management Actions =====

/**
 * Get posts for a location
 */
export async function getPosts(locationId: string, pageSize: number = 10, pageToken?: string) {
  return callGMBWorker<{
    posts: Array<{
      name: string
      languageCode: string
      summary: string
      state: 'LIVE' | 'REMOVED' | 'REJECTED'
      topicType: 'STANDARD' | 'EVENT' | 'OFFER' | 'PRODUCT'
      createTime: string
      updateTime: string
      callToAction?: {
        actionType: string
        url?: string
      }
      media?: Array<{
        mediaFormat: 'PHOTO' | 'VIDEO'
        sourceUrl: string
      }>
      event?: {
        title: string
        schedule: {
          startDate: { year: number; month: number; day: number }
          startTime?: { hours: number; minutes: number; seconds: number; nanos: number }
          endDate?: { year: number; month: number; day: number }
          endTime?: { hours: number; minutes: number; seconds: number; nanos: number }
        }
      }
      offer?: {
        couponCode?: string
        redeemOnlineUrl?: string
        termsConditions?: string
      }
      insights?: {
        viewCount: number
        searchCount: number
      }
    }>
    nextPageToken?: string
  }>('get_posts', {
    location_id: locationId,
    page_size: pageSize,
    page_token: pageToken
  })
}

/**
 * Create a new post
 */
export async function createPost(
  locationId: string, 
  post: {
    languageCode?: string
    summary: string
    topicType: 'STANDARD' | 'EVENT' | 'OFFER' | 'PRODUCT'
    callToAction?: {
      actionType: 'BOOK' | 'ORDER' | 'SHOP' | 'LEARN_MORE' | 'SIGN_UP' | 'CALL'
      url?: string
    }
    media?: Array<{
      mediaFormat: 'PHOTO' | 'VIDEO'
      sourceUrl: string
    }>
    event?: {
      title: string
      schedule: {
        startDate: { year: number; month: number; day: number }
        startTime?: { hours: number; minutes: number; seconds: number; nanos: number }
        endDate?: { year: number; month: number; day: number }
        endTime?: { hours: number; minutes: number; seconds: number; nanos: number }
      }
    }
    offer?: {
      couponCode?: string
      redeemOnlineUrl?: string
      termsConditions?: string
    }
  }
) {
  return callGMBWorker<{
    name: string
    state: string
    createTime: string
  }>('create_post', {
    location_id: locationId,
    post: {
      languageCode: post.languageCode || 'en',
      ...post
    }
  })
}

/**
 * Update an existing post
 */
export async function updatePost(
  postId: string, 
  post: {
    languageCode?: string
    summary: string
    topicType: 'STANDARD' | 'EVENT' | 'OFFER' | 'PRODUCT'
    callToAction?: {
      actionType: string
      url?: string
    }
    media?: Array<{
      mediaFormat: 'PHOTO' | 'VIDEO'
      sourceUrl: string
    }>
  }
) {
  return callGMBWorker<{
    name: string
    state: string
    updateTime: string
  }>('update_post', {
    post_id: postId,
    post
  })
}

/**
 * Delete a post
 */
export async function deletePost(postId: string) {
  return callGMBWorker<{
    success: boolean
  }>('delete_post', {
    post_id: postId
  })
}

// ===== Analytics & Performance Actions =====

/**
 * Get legacy insights (may be deprecated)
 */
export async function getInsights(locationId: string, metricRequests?: any[]) {
  return callGMBWorker('get_insights', {
    location_id: locationId,
    metric_requests: metricRequests
  })
}

/**
 * Get performance metrics using new Performance API
 */
export async function getPerformanceMetrics(
  locationId: string,
  startDate: string,
  endDate: string
) {
  return callGMBWorker<{
    metrics: Array<{
      metric: string
      values: Array<{
        date: string
        value: number
      }>
    }>
  }>('get_performance_metrics', {
    location_id: locationId,
    start_date: startDate,
    end_date: endDate
  })
}

/**
 * Get search keywords insights
 */
export async function getSearchKeywords(locationId: string) {
  return callGMBWorker<{
    keywords: Array<{
      keyword: string
      impressions: number
    }>
  }>('get_search_keywords', {
    location_id: locationId
  })
}

// ===== Q&A Management Actions =====

/**
 * Get questions for a location
 */
export async function getQuestions(locationId: string, pageSize: number = 10, pageToken?: string) {
  return callGMBWorker<{
    questions: Array<{
      name: string
      author: {
        displayName: string
        profilePhotoUri?: string
        type: 'REGULAR_USER' | 'LOCAL_GUIDE' | 'MERCHANT'
      }
      text: string
      createTime: string
      updateTime: string
      upvoteCount: number
      answers?: Array<{
        name: string
        author: {
          displayName: string
          profilePhotoUri?: string
          type: 'REGULAR_USER' | 'LOCAL_GUIDE' | 'MERCHANT'
        }
        text: string
        createTime: string
        updateTime: string
        upvoteCount: number
      }>
      totalAnswerCount: number
    }>
    totalSize?: number
    nextPageToken?: string
  }>('get_questions', {
    location_id: locationId,
    page_size: pageSize,
    page_token: pageToken
  })
}

/**
 * Answer a customer question
 */
export async function answerQuestion(
  locationId: string,
  questionId: string,
  answer: string
) {
  return callGMBWorker<{
    name: string
    text: string
    createTime: string
  }>('answer_question', {
    location_id: locationId,
    question_id: questionId,
    answer
  })
}

// ===== Media Management Actions =====

/**
 * Get media (photos/videos) for a location
 */
export async function getMedia(locationId: string) {
  return callGMBWorker<{
    mediaItems: Array<{
      name: string
      mediaFormat: 'PHOTO' | 'VIDEO'
      sourceUrl: string
      description?: string
      createTime: string
    }>
  }>('get_media', {
    location_id: locationId
  })
}

// ===== Helper Functions =====

/**
 * Convert star rating enum to number
 */
export function convertStarRating(rating: string): number {
  const ratingMap: Record<string, number> = {
    'ONE': 1,
    'TWO': 2,
    'THREE': 3,
    'FOUR': 4,
    'FIVE': 5
  }
  return ratingMap[rating] || 0
}

/**
 * Format GMB location for display
 */
export function formatGMBLocation(location: any) {
  return {
    id: location.name,
    name: location.locationName || 'Unknown Location',
    address: location.address ? 
      [
        location.address.addressLines?.join(', '),
        location.address.locality,
        location.address.administrativeArea,
        location.address.postalCode
      ].filter(Boolean).join(', ') : 
      'No address available',
    phone: location.primaryPhone,
    website: location.websiteUri,
    placeId: location.placeId,
    coordinates: location.latlng
  }
}

/**
 * Check if OAuth is configured
 */
export async function checkOAuthStatus() {
  const result = await getStoredAccounts()
  return {
    isConfigured: result.success && result.data?.accounts?.length > 0,
    lastSync: result.data?.last_sync,
    accountCount: result.data?.accounts?.length || 0,
    locationCount: result.data?.locations?.length || 0
  }
}

/**
 * Map GMB location to franchise region based on business rules
 * Toronto locations map to North York, all others map to Hamilton
 */
export function mapLocationToFranchise(locationName: string, address?: string): string {
  if (!locationName) {
    console.warn('mapLocationToFranchise called with undefined/null locationName')
    return 'hamilton' // Default fallback
  }
  const locationLower = locationName.toLowerCase()
  const addressLower = (address || '').toLowerCase()
  
  // Check if it's a Toronto location
  if (locationLower.includes('toronto') || addressLower.includes('toronto')) {
    return 'north_york'
  }
  
  // All other locations default to Hamilton
  return 'hamilton'
}

/**
 * Batch operations helper - process items in chunks with rate limiting
 */
export async function processBatchWithRateLimit<T, R>(
  items: T[],
  batchSize: number,
  delayMs: number,
  processor: (batch: T[]) => Promise<R>
): Promise<R[]> {
  const results: R[] = []
  
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize)
    const result = await processor(batch)
    results.push(result)
    
    // Add delay between batches to avoid rate limits
    if (i + batchSize < items.length) {
      await new Promise(resolve => setTimeout(resolve, delayMs))
    }
  }
  
  return results
}

/**
 * Retry wrapper for GMB API calls
 */
export async function retryGMBCall<T>(
  fn: () => Promise<GMBWorkerResponse<T>>,
  maxRetries: number = 3,
  backoffMs: number = 1000
): Promise<GMBWorkerResponse<T>> {
  let lastError: any
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await fn()
      
      // If successful or non-retryable error, return immediately
      if (result.success || 
          (result.error && !result.error.includes('rate limit') && !result.error.includes('timeout'))) {
        return result
      }
      
      lastError = result.error
    } catch (error) {
      lastError = error
    }
    
    // Wait before retrying with exponential backoff
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, backoffMs * Math.pow(2, attempt)))
    }
  }
  
  return {
    success: false,
    error: lastError?.message || lastError || 'Max retries exceeded',
    message: 'Failed after multiple retry attempts'
  }
}

/**
 * Format date for GMB API (YYYY-MM-DD)
 */
export function formatGMBDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

/**
 * Parse GMB timestamp to Date
 */
export function parseGMBTimestamp(timestamp: string): Date {
  return new Date(timestamp)
}

/**
 * Get all reviews for a location (handles pagination)
 */
export async function getAllReviews(locationId: string): Promise<any[]> {
  const allReviews: any[] = []
  let pageToken: string | undefined
  
  do {
    const result = await getReviews(locationId, 50, pageToken)
    if (result.success && result.data?.reviews) {
      allReviews.push(...result.data.reviews)
      pageToken = result.data.nextPageToken
    } else {
      break
    }
  } while (pageToken)
  
  return allReviews
}

/**
 * Get all posts for a location (handles pagination)
 */
export async function getAllPosts(locationId: string): Promise<any[]> {
  const allPosts: any[] = []
  let pageToken: string | undefined
  
  do {
    const result = await getPosts(locationId, 25, pageToken)
    if (result.success && result.data?.posts) {
      allPosts.push(...result.data.posts)
      pageToken = result.data.nextPageToken
    } else {
      break
    }
  } while (pageToken)
  
  return allPosts
}

/**
 * Get all questions for a location (handles pagination)
 */
export async function getAllQuestions(locationId: string): Promise<any[]> {
  const allQuestions: any[] = []
  let pageToken: string | undefined
  
  do {
    const result = await getQuestions(locationId, 10, pageToken)
    if (result.success && result.data?.questions) {
      allQuestions.push(...result.data.questions)
      pageToken = result.data.nextPageToken
    } else {
      break
    }
  } while (pageToken)
  
  return allQuestions
}